<template>
  <sp-modal
    class="required-filters-modal"
    :class="breakpointModifiers"
    :open="open"
    persistent
    expand-height-max
    width="36rem"
    :data-testid="TestId.finder.requiredFiltersModal"
  >
    <div slot="header" class="required-filters-modal__header">
      <h4 class="required-filters-modal__title">{{ currentTitle }}</h4>
    </div>

    <!--
      Only render the steps once the modal opens: DistanceFilter fetches its
      location data in `onMounted`, so mounting it eagerly while the modal stays
      closed fires an unnecessary `common_places` request.
    -->
    <div v-if="open" class="required-filters-modal__content">
      <!--
        Location step uses the near-filter content directly (without the filter
        activator wrapper) so the modal matches the expected UX. Gated on
        `!skipLocation`: the step-1 wrapper uses `v-show` (mounts even when
        hidden), and DistanceFilter fetches `common_places` in `onMounted`, so
        when a location is already present and the modal opens directly on the
        insurance step, mounting it would fire that request for a step the user
        never sees. `v-if="open"` alone doesn't cover this (the modal is open).
      -->
      <div v-show="step === 1" class="required-filters-modal__step">
        <DistanceFilter
          v-if="!skipLocation"
          permanent
          :label="LOCATION_LABEL"
          :url="locationUrl"
          :distance="defaultDistance"
          :distances="distances"
          :distance-control="distanceControl"
          :allow-any="allowAnyDistance"
          :search-placeholder="searchPlaceholder"
          location-pin-background-color="var(--sp-sys-color-primary)"
          @update:model-value="handleLocationModelUpdate"
        ></DistanceFilter>
      </div>

      <!-- Insurance step. The selected plan value is captured the same way. -->
      <div v-if="step === 2" class="required-filters-modal__step required-filters-modal__step--insurance">
        <InsurancePlanGroupedFilter
          :label="INSURANCE_LABEL"
          :url="insuranceUrl"
          :option-test-id="TestId.finder.requiredFiltersPlanOption"
          @update:model-value="handleInsuranceModelUpdate"
        ></InsurancePlanGroupedFilter>
      </div>
    </div>

    <div slot="actions" class="required-filters-modal__actions">
      <div v-if="showBtnSpacer" class="required-filters-modal__btn-spacer"></div>
      <sp-button v-if="showBackButton" variant="outlined" class="required-filters-modal__btn-back" @click="goBack">{{
        BACK_LABEL
      }}</sp-button>
      <sp-button
        :disabled="!canContinue"
        class="required-filters-modal__btn-continue"
        :data-testid="TestId.finder.requiredFiltersContinue"
        @click="goNext"
        >{{ CONTINUE_LABEL }}</sp-button
      >
    </div>
  </sp-modal>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import DistanceFilter from "#ce/shared/filters/distance/components/DistanceFilter.ce.vue";
import InsurancePlanGroupedFilter from "#ce/shared/filters/insurance-plan/components/InsurancePlanGroupedFilter.ce.vue";
import { useInsurancePlanValidation } from "#ce/shared/filters/insurance-plan/composables/useInsurancePlanValidation";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useFinder } from "#ce/composables/useFinder";
import { useHostElement } from "#ce/composables/useHostElement";
import { resolveFinder } from "#ce/shared/finder-association";
import { toBoolean, trimToUndefined } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";
import { navigateTo } from "#/shared/lib/navigation";
import { TestId } from "#/test-ids";
import { props as modalProps } from "./props.js";

// The nested DistanceFilter / InsurancePlanGroupedFilter attach ElementInternals via useForm() on this app's host.
defineOptions(formAssociatedHost());

// Matches DoctorFinder.INSURANCE_PLAN_SESSION_STORAGE_KEY so the finder
// auto-applies the chosen plan on the results page.
const INSURANCE_PLAN_SESSION_STORAGE_KEY = "insurancePlanFilter";
// Matches DoctorFinder.LOCATION_SESSION_STORAGE_KEY so the finder
// auto-applies the persisted location on the results page.
const LOCATION_SESSION_STORAGE_KEY = "locationFilter";

const INSURANCE_NAME = "insurance_plan";
const LOCATION_STEP_TITLE = "Where are you located?";
const INSURANCE_STEP_TITLE = "Now select your plan";
const CONTINUE_LABEL = "Continue";
const BACK_LABEL = "Back";
const LOCATION_LABEL = "Location";
const INSURANCE_LABEL = "Insurance plan";

const props = defineProps(modalProps);

const host = useHostElement();
const finderElement = host?.closest("sp-provider-finder");
const { attached } = useFinder();

function finderWrapper() {
  return resolveFinder(host).element;
}

// A wrapper that has not upgraded yet takes the hold and its release as a plain property, which it adopts on upgrade.
function holdFinderFetching() {
  const wrapper = finderWrapper();

  if (wrapper?.holdFetching) {
    wrapper.holdFetching();
  } else if (wrapper) {
    wrapper.fetchingHeld = true;
  }
}

function releaseFinderFetching(options) {
  const wrapper = finderWrapper();

  if (wrapper?.releaseFetching) {
    wrapper.releaseFetching(options);
  } else if (wrapper) {
    wrapper.fetchingHeld = false;
    wrapper.pendingRelease = options ?? {};
  }
}

// Trust a persisted filter value only when this finder opted into persisting it (and is attached — the
// settings read false until the legacy finder attaches). Mirrors the finder, which gates
// selectedLocation()/selectedInsurancePlan() on the same flags, so a value another finder left in the
// shared tab storage can't satisfy a required step here. The URL still bootstraps regardless.
//
// Standalone mode (detail pages) has no finder, so the client passes the flags in as props — without
// this the storage-first readers would never trust the values just persisted and the modal would reopen.
function persistLocationEnabled() {
  if (!finderElement) {
    return toBoolean(props.persistLocation);
  }
  return Boolean(finderWrapper()?.persistSettings?.().persistLocation);
}

function persistInsurancePlanEnabled() {
  if (!finderElement) {
    return toBoolean(props.persistInsurancePlan);
  }
  return Boolean(finderWrapper()?.persistSettings?.().persistInsurancePlan);
}

const { breakpointModifiers } = useBreakpoints();

const open = ref(false);
const step = ref(1);

const locationData = ref(null);
const insuranceValue = ref(undefined);

const currentTitle = computed(() => (step.value === 1 ? LOCATION_STEP_TITLE : INSURANCE_STEP_TITLE));

const showBtnSpacer = computed(() => step.value === 1 || skipLocation.value);
const showBackButton = computed(() => step.value === 2 && !skipLocation.value);

const step1Valid = computed(() => {
  const data = locationData.value;
  if (!data) {
    return false;
  }
  return Boolean(data.get("near")) || Boolean(data.get("lat") && data.get("lng"));
});

const step2Valid = computed(() => Boolean(insuranceValue.value));

const canContinue = computed(() => (step.value === 1 ? step1Valid.value : step2Valid.value));

function handleLocationModelUpdate(value) {
  locationData.value = value ?? null;
}

function handleInsuranceModelUpdate(value) {
  insuranceValue.value = trimToUndefined(value);
}

/**
 * Build a plain location object from the distance filter's FormData. The
 * distance filter emits `near`, which the finder treats as `zip`.
 */
function locationFilters() {
  const data = locationData.value;
  if (!data) {
    return {};
  }

  return Object.fromEntries(
    Object.entries({
      zip: data.get("near"),
      lat: data.get("lat"),
      lng: data.get("lng"),
      distance: data.get("distance"),
    }).filter(([, value]) => value != null && value !== ""),
  );
}

/**
 * Persist the captured selections to sessionStorage in standalone mode (no
 * finder, e.g. detail pages). Uses the same keys/shape as the finder and honours
 * the client's persist settings.
 */
function persistFiltersStandalone(location, insurance) {
  if (insurance && persistInsurancePlanEnabled()) {
    try {
      window.sessionStorage.setItem(INSURANCE_PLAN_SESSION_STORAGE_KEY, insurance);
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
  }

  if (Object.keys(location).length > 0 && persistLocationEnabled()) {
    // When the user picks "Use my location" there is no zip, so fall back to a
    // generic label, matching the finder's geolocated label behaviour.
    const locationDisplayName = location.zip ?? "Current Location";
    try {
      window.sessionStorage.setItem(
        LOCATION_SESSION_STORAGE_KEY,
        JSON.stringify({
          zip: location.zip,
          distance: location.distance,
          lat: location.lat,
          lng: location.lng,
          locationDisplayName,
        }),
      );
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
  }
}

/**
 * Standalone mode has no finder to bootstrap URL filters into sessionStorage, so
 * the modal does it: a filter present in the URL but not yet stored is persisted
 * for the tab (mirroring the finder), so a deep link with `?zip=…&insurance_plan=…`
 * survives navigation to later param-less pages instead of reopening the modal.
 */
function persistUrlBootstrapStandalone() {
  // The finder handles this on its own pages.
  if (finderElement) {
    return;
  }

  let storedLocation;
  let storedInsurance;
  try {
    storedLocation = window.sessionStorage.getItem(LOCATION_SESSION_STORAGE_KEY);
    storedInsurance = window.sessionStorage.getItem(INSURANCE_PLAN_SESSION_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable.
    return;
  }

  // Only bootstrap a filter the tab hasn't already stored. `*FilterValue()` is
  // storage-first, so with storage empty it returns the URL value; leaving a
  // stored value untouched preserves its display name and distance.
  const location = storedLocation ? {} : (locationFilterValue() ?? {});
  const insurance = trimToUndefined(storedInsurance) ? undefined : insuranceFilterValue();

  persistFiltersStandalone(location, insurance);
}

/**
 * Apply the captured selections to the doctor finder through the wrapper. On the
 * landing page the filters are pre-filled without submitting the search; on the
 * results page they are applied to the active search.
 */
function applyToFinder() {
  const location = locationFilters();
  const insurance = insuranceValue.value;

  if (!attached.value) {
    // Standalone mode (detail pages): no finder to drive, so just persist the
    // selections (honouring the client's persist settings). The caller handles
    // navigation separately.
    persistFiltersStandalone(location, insurance);
    return;
  }

  // Persist the chosen insurance plan to sessionStorage, but only when the
  // client has opted in via `persist_insurance_plan`. Persisting unconditionally
  // would violate clients that disable it and make the modal treat them as having
  // a pre-selected plan on later visits.
  const persistInsurancePlan = persistInsurancePlanEnabled();
  if (insurance && persistInsurancePlan) {
    try {
      window.sessionStorage.setItem(INSURANCE_PLAN_SESSION_STORAGE_KEY, insurance);
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
  }

  finderWrapper()?.applyRequiredFilters?.({ location, insurance });
}

/**
 * In standalone mode the detail page scopes its content server-side from the
 * `zip` and `insurance_plan` URL params (ProvidersHelper#searched_location /
 * #searched_insurance_plan), so navigate to the current path with the selected
 * filters in the query string rather than a plain reload (which would keep the
 * param-less URL). Falls back to the already-effective value for a skipped step.
 */
function reloadWithFilters() {
  const capturedLocation = locationFilters();
  const location = Object.keys(capturedLocation).length > 0 ? capturedLocation : (locationFilterValue() ?? {});
  const insurance = insuranceValue.value ?? insuranceFilterValue();

  const params = new URLSearchParams(window.location.search);
  // Replace any existing location/insurance params.
  for (const key of ["zip", "near", "lat", "lng", "distance", "insurance_plan"]) {
    params.delete(key);
  }
  if (location.zip != null) {
    params.set("zip", location.zip);
  }
  // Coordinates are only meaningful as a complete pair.
  if (location.lat != null && location.lng != null) {
    params.set("lat", location.lat);
    params.set("lng", location.lng);
  }
  if (location.distance != null) {
    params.set("distance", location.distance);
  }
  if (insurance) {
    params.set("insurance_plan", insurance);
  }

  // Build an absolute URL from the current origin. A path-relative URL passed to
  // location.assign() resolves against the document base URL, which a `<base href>`
  // on published pages points at the production domain — navigating off-site.
  const url = new URL(`${window.location.origin}${window.location.pathname}`);
  url.search = params.toString();
  navigateTo(url.toString());
}

function goNext() {
  if (step.value === 1 && !skipInsurance.value) {
    step.value = 2;
    return;
  }

  const standalone = !attached.value;
  applyToFinder();
  close();
  // In standalone mode (detail pages) navigate with the selected filters in the
  // URL so the server-side scoping re-renders the page for the new selection.
  // The URL params satisfy the gate after reload regardless of the persist
  // settings, so this is independent of whether the selection was stored.
  if (standalone) {
    reloadWithFilters();
  }
}

function goBack() {
  step.value = 1;
}

function openModal() {
  open.value = true;
}

function close() {
  open.value = false;
  releaseFinderFetching();
}

/**
 * Whether insurance is already available (from URL or sessionStorage). When
 * present we skip the insurance step entirely.
 */
const skipInsurance = ref(false);

/**
 * Whether a location is already available (from URL or sessionStorage). When
 * present we skip the location step entirely and open directly on the insurance
 * step.
 */
const skipLocation = ref(false);

/**
 * The location filter already selected for this page, read from the URL or
 * sessionStorage. Returns `undefined` when none is present. The shape mirrors
 * DoctorFinder's persisted location object (`zip`/`lat`/`lng`/`distance`),
 * where `zip` is what the distance filter emits as `near`.
 */
function locationFilterValue() {
  // Storage-first, but only when this finder opted into persisting the location (persistLocationEnabled);
  // otherwise a location another finder left in the shared tab storage could wrongly satisfy the required
  // step. The URL bootstraps regardless.
  if (persistLocationEnabled()) {
    try {
      const stored = window.sessionStorage.getItem(LOCATION_SESSION_STORAGE_KEY);
      if (stored) {
        const location = JSON.parse(stored);
        // Usable = a zip or a complete coordinate pair (matches the finder's selectedLocation()).
        if (location && (location.zip != null || (location.lat != null && location.lng != null))) {
          return location;
        }
      }
    } catch {
      // Corrupt stored value — drop it so later checks don't keep re-parsing the poisoned key (matches
      // the finder's selectedLocation()), then fall through to the URL.
      try {
        window.sessionStorage.removeItem(LOCATION_SESSION_STORAGE_KEY);
      } catch {
        // sessionStorage unavailable — nothing to clear.
      }
    }
  }

  const params = new URLSearchParams(window.location.search);
  const zip = trimToUndefined(params.get("zip")) ?? trimToUndefined(params.get("near"));
  const lat = trimToUndefined(params.get("lat"));
  const lng = trimToUndefined(params.get("lng"));
  const hasCoordinates = Boolean(lat && lng);
  if (zip || hasCoordinates) {
    // Coordinates are only meaningful as a complete pair; never carry a lone lat/lng (e.g. ?zip=…&lat=…
    // with no lng), matching the finder's locationParamsFromUrl().
    const location = { zip, distance: trimToUndefined(params.get("distance")) };
    if (hasCoordinates) {
      location.lat = lat;
      location.lng = lng;
    }
    return location;
  }
  return undefined;
}

/**
 * Build the distance filter's FormData representation from a stored location
 * object so a skipped location step is still applied when the modal completes.
 * The distance filter uses `near` for what the finder persists as `zip`.
 */
function locationDataFromValue(location) {
  if (!location) {
    return null;
  }
  const data = new FormData();
  if (location.zip != null) {
    data.set("near", location.zip);
  }
  if (location.lat != null) {
    data.set("lat", location.lat);
  }
  if (location.lng != null) {
    data.set("lng", location.lng);
  }
  if (location.distance != null) {
    data.set("distance", location.distance);
  }
  return data;
}

/**
 * The persisted insurance plan candidate, read from sessionStorage — but only when this finder opted into
 * persisting the plan; otherwise a plan another finder left in the shared tab storage could wrongly skip
 * the required step. Returns `undefined` when none is present. Presence only: whether the slug is a *real*
 * plan is decided by the validated open/skip path (evaluateAndOpen), per source.
 */
function storedInsuranceValue() {
  if (!persistInsurancePlanEnabled()) {
    return undefined;
  }
  try {
    const stored = window.sessionStorage.getItem(INSURANCE_PLAN_SESSION_STORAGE_KEY);
    return stored == null ? undefined : trimToUndefined(stored);
  } catch {
    return undefined; // sessionStorage unavailable
  }
}

/**
 * The insurance plan candidate from the URL. Returns `undefined` when none is present.
 */
function urlInsuranceValue() {
  const value = new URLSearchParams(window.location.search).get(INSURANCE_NAME);
  return value == null ? undefined : trimToUndefined(value);
}

/**
 * The insurance plan already effective for this page, read storage-first (persist-gated) then URL.
 * Returns `undefined` when none is present. Presence-based — any non-blank value is used as-is, without
 * validating that it is a known plan slug (the validated open/skip path, evaluateAndOpen, owns that).
 * Used by the standalone (detail-page) helpers, which have no finder to drive the validated path.
 */
function insuranceFilterValue() {
  return storedInsuranceValue() ?? urlInsuranceValue();
}

// Raw presence of an insurance plan in sessionStorage — read directly (not persist-gated), because this
// gates whether the preflight runs at mount, before the legacy finder (and thus the persist flags) is
// necessarily attached. Over-triggering when persist is off is harmless: the validation is persist-aware.
function hasStoredInsurance() {
  try {
    return Boolean(window.sessionStorage.getItem(INSURANCE_PLAN_SESSION_STORAGE_KEY));
  } catch {
    return false;
  }
}

// `onInvalid` callbacks for plan validation: drop a slug that loaded fine but isn't a real plan from one
// specific source. Source-specific so an invalid *stored* plan is cleared from storage only — leaving a
// valid URL plan to still win — rather than wiping both sources at once.
function clearStoredInsurance() {
  try {
    window.sessionStorage.removeItem(INSURANCE_PLAN_SESSION_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable; ignore.
  }
}

function clearUrlInsurance() {
  const url = new URL(window.location.href);
  if (url.searchParams.has(INSURANCE_NAME)) {
    url.searchParams.delete(INSURANCE_NAME);
    // Use an absolute same-origin URL. A root-relative URL can resolve against the page's <base href>,
    // which embedded finder pages may point at the public host instead of the current dev/test origin.
    try {
      window.history.replaceState(window.history.state, "", url.href);
    } catch {
      // History updates can fail in embedded/sandboxed contexts; validation should still continue.
    }
  }
}

const { resolveFirstValid: resolveValidInsurance } = useInsurancePlanValidation({ url: props.insuranceUrl });

/**
 * Open the modal after the finder has initialized. Sets
 * the wrapper's fetch hold immediately so
 * that `showDoctorFinder` pauses fetching right after constructing the
 * DoctorFinder (before the initial route handler can trigger a network request).
 *
 * Waits for wrapper attachment so the URL/sessionStorage state is settled
 * before deciding whether to open.
 */
function autoOpenAfterFinderReady() {
  // Eagerly block fetching before the finder JS even loads. We always block while the preflight runs (not
  // just when a filter looks absent): the finder applies storage-first state the server-rendered gate
  // never saw, so we can't trust it is valid until evaluateAndOpen() validates and either opens or
  // reconciles + resumes below.
  holdFinderFetching();

  async function evaluateAndOpen() {
    // Validate the insurance candidates against the plan list, storage-first, so a fake/stale slug (e.g. a
    // hand-typed `?insurance_plan=not-a-plan`) doesn't satisfy the required-filter check or silently skip the
    // step. Validating per source means an invalid *stored* plan is cleared from storage only, letting a
    // valid URL plan still win. Fails closed: an unvalidatable list (timeout/error) clears nothing and
    // treats insurance as absent, opening the modal.
    const validInsurance = await resolveValidInsurance([
      { value: storedInsuranceValue(), onInvalid: clearStoredInsurance },
      { value: urlInsuranceValue(), onInvalid: clearUrlInsurance },
    ]);
    const hasValidInsurance = validInsurance !== undefined;
    // Read the location once (it also clears a corrupt stored value — idempotent, so once is enough).
    const location = locationFilterValue();
    const hasLocation = Boolean(location);

    if (hasLocation && hasValidInsurance) {
      // Required filters present and valid — unblock fetching, don't open the modal
      releaseFinderFetching({ insurancePlan: validInsurance });
      return;
    }

    skipInsurance.value = hasValidInsurance;
    skipLocation.value = hasLocation;
    // When the insurance step is skipped, seed the validated value so it is still applied
    // to the finder when only the location step is completed.
    if (skipInsurance.value) {
      insuranceValue.value = validInsurance;
    }
    // When a location is already present, seed it and open directly on the
    // insurance step so the persisted location is still applied on completion.
    if (skipLocation.value) {
      locationData.value = locationDataFromValue(location);
      step.value = 2;
    }
    openModal();
  }

  if (!finderElement) {
    evaluateAndOpen();
    return;
  }

  if (attached.value) {
    evaluateAndOpen();
    return;
  }

  const stopWaiting = watch(attached, function handleAttachment(isAttached) {
    if (isAttached) {
      stopWaiting();
      evaluateAndOpen();
    }
  });
}

onMounted(() => {
  // Persist any URL-only filters first, so a deep link that satisfies the gate
  // (and keeps the modal closed) still remembers the selections for the visit.
  persistUrlBootstrapStandalone();
  // Run the preflight when the server flagged a missing/invalid URL filter (auto-open) OR whenever there
  // is any insurance left in sessionStorage — a value the server's URL-only gate could not see, so the
  // client must validate it even when the server thought the URL alone was fine. (Persist-gating still
  // decides whether that stored value is actually trusted, inside the validation.)
  if (toBoolean(props.autoOpen) || hasStoredInsurance()) {
    autoOpenAfterFinderReady();
  }
});
</script>

<style>
:host {
  display: contents;
}
</style>

<style lang="scss" scoped>
.required-filters-modal {
  // The card fills the surface: dialog content and card height are 100% so the
  // card's percentage height resolves against a definite parent — the
  // fixed-height dialog on large screens, the expanded bottom sheet on small.
  --sp-comp-dialog-content-height: 100%;
  --sp-comp-modal-card-height: 100%;
  --sp-comp-modal-header-background-color: white;
  --sp-comp-modal-header-color: black;
  --sp-comp-card-title-background-color: transparent;
  --sp-comp-card-title-color: var(--sp-sys-color-on-surface);

  // Brand values for the modal's action buttons. Locally defined, so the usages
  // below reference them without a fallback.
  --required-filters-modal-color-primary: #5bb45d;
  --required-filters-modal-color-primary-hover: #4a9e4c;
  --required-filters-modal-color-primary-disabled: #a8d5a9;
  --required-filters-modal-color-text: #303133;
  --required-filters-modal-button-radius: 100px;

  // Fixed-height sizing applies to the dialog surface only. On small screens
  // the modal renders on the bottom sheet, which owns the viewport sizing
  // (expand-height-max); a vh-based cap can fall short of the sheet and leave
  // dead space inside the card, so the card tracks the sheet via sp-modal's
  // default instead.
  &:not(.--is-sm-screen) {
    --required-filters-modal-height: min(85vh, 60rem);
    --dialog-height: var(--required-filters-modal-height);
    --dialog-max-height: var(--required-filters-modal-height);
    --sp-comp-modal-card-max-height: var(--required-filters-modal-height);
  }

  &.--is-sm-screen {
    --sp-comp-bottom-sheet-content-padding-inline: 0;
    --sp-comp-bottom-sheet-content-padding-block: 0;
    // Single source of truth for the modal's inline padding. This drives the
    // header, card content and actions wrappers alike, so they all share the
    // same left/right padding as .sp-card-content on mobile.
    --sp-comp-modal-padding: var(--sp-ref-scale-16);

    .required-filters-modal__actions {
      flex-direction: column;
      padding-block-end: var(--sp-ref-scale-8);
    }

    .required-filters-modal__btn-spacer {
      display: none;
    }
  }
}

.required-filters-modal__header {
  padding: var(--sp-ref-scale-16) 0 0;
}

.required-filters-modal__title {
  margin: 0;
  font-family: var(--sp-sys-typescale-h3-font-family);
  font-weight: var(--sp-sys-font-weight-bold);
  font-size: 1.25rem;
  line-height: 2rem;
  color: var(--sp-sys-color-on-surface);
}

.required-filters-modal__step {
  display: block;

  // Bold the "Popular Locations" subheader in the nested quantum-select.
  --sp-comp-list-subheader-font-weight: var(--sp-sys-font-weight-bold);

  // Inverted location pins: white glyph on the violet circle set via the
  // DistanceFilter `location-pin-background-color` prop above. Shrink the circle
  // (icon-size) while bumping the glyph ratio so the pin keeps its size: the glyph
  // is `icon-size * ratio`, so 1.5rem * 0.8 == the previous 2rem * 0.6 (1.2rem).
  --sp-comp-distance-filter-list-item-pin-fill-color: var(--sp-sys-color-on-primary);
  --sp-comp-distance-filter-list-item-icon-size: 1.5rem;
  --sp-comp-list-item-prepend-bg-icon-ratio: 0.8;
  // The default `start` alignment top-aligned the pin; now that the circle is
  // shorter than the row, centre it so it lines up with the row text.
  --sp-comp-distance-filter-list-item-align-items: center;

  .distance-filter {
    --sp-comp-popup-min-height: auto;
    --sp-comp-card-content-height: auto;
    --sp-comp-distance-filter-padding-inline: 0;

    // Render the "Near" title and "Use my location" labels in the brand font.
    // DistanceFilter exposes these family hooks; it owns the shorthand re-declaration
    // internally, so we only override the family here.
    --sp-comp-distance-filter-title-font-family: var(--sp-sys-font-family-black);
    --sp-comp-distance-filter-current-location-font-family: var(--sp-sys-font-family-black);
  }

  :deep(sp-card-content) {
    height: auto;
  }
}

.required-filters-modal__step--insurance {
  // The insurance step renders the grouped radio-card layout
  // (<sp-radio-card-group>), which manages its own card padding and inter-card
  // gaps — no shell offset needed here.
  display: block;
  height: 100%;
}

.required-filters-modal__content {
  min-height: 0;
  overflow: hidden;
}

.required-filters-modal__actions {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  gap: var(--sp-ref-scale-12);
  padding: 0 0 var(--sp-ref-scale-24);
}

.required-filters-modal__btn-spacer {
  flex: 1;
}

.required-filters-modal__btn-back {
  flex: 1;
  --sp-comp-button-font-size: var(--sp-sys-typescale-body-font-size);
  --sp-comp-button-font-weight: var(--sp-sys-font-weight-bold);
  --sp-comp-button-border-radius: var(--required-filters-modal-button-radius);
  --sp-comp-button-outlined-border-color: var(--required-filters-modal-color-primary);
  --sp-comp-button-primary-bg-color: transparent;
  --sp-comp-button-primary-text-color: var(--required-filters-modal-color-text);
  --sp-comp-button-primary-outlined-text-color: var(--required-filters-modal-color-text);
  --sp-comp-button-primary-hover-outlined-text-color: var(--required-filters-modal-color-text);
}

.required-filters-modal__btn-continue {
  flex: 1;
  --sp-comp-button-font-size: var(--sp-sys-typescale-body-font-size);
  --sp-comp-button-font-weight: var(--sp-sys-font-weight-bold);
  --sp-comp-button-border-radius: var(--required-filters-modal-button-radius);
  --sp-comp-button-primary-bg-color: var(--required-filters-modal-color-primary);
  --sp-comp-button-primary-text-color: white;
  --sp-comp-button-border-color: var(--required-filters-modal-color-primary);
  --sp-comp-button-primary-hover-bg-color: var(--required-filters-modal-color-primary-hover);
  --sp-comp-button-disabled-background-color: var(--required-filters-modal-color-primary-disabled);
  --sp-comp-button-disabled-text-color: white;
}
</style>
